import type AppTheme from '../../Enums/AppTheme';
import type Mode from '../../Enums/Mode';
import { datetimeStandardParser, datetimeToApiString } from '../../Utils/DateTimeParser';
import LocalStorageKey from '../../Enums/LocalStorageKey';
import CurrentUser from '../../Models/CurrentUserModel';

class LocalStorageService {

    private _id: string;

    public constructor(id: string) {
        this._id = id;
    }

    public get id(): string {
        return this._id;
    }

    public set id(value: string) {
        this._id = value;
    }

    public static isLocalStorageAvailable(): boolean {
    
        const test: string = 'test';
        
        try {
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch(e) {
            return false;
        }
    }

    public static clearAllData(): void {

        localStorage.clear();
    }

    public getMode(): Mode | null {

        try {
            const stringMode: string | null = localStorage.getItem(this._id + LocalStorageKey.MODE);

            if (stringMode === null) {
                return null;
            }

            return JSON.parse(stringMode);
        } catch (e: unknown) {
            return null;
        }
    }

    public setMode(mode: Mode): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.MODE, JSON.stringify(mode));
            
            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearMode(): boolean {

        try {   
            localStorage.removeItem(this._id + LocalStorageKey.MODE);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getCurrentUser(): CurrentUser | null {

        try {
            const email: string | null = localStorage.getItem(this._id + LocalStorageKey.EMAIL);
            const accessString: string | null = localStorage.getItem(this._id + LocalStorageKey.ACCESS);
            
            if (email === null || accessString === null) {
                return null;
            }

            const access: string[] = accessString.split(',');
            
            return new CurrentUser(email, access);
        } catch (e: unknown) {
            return null;
        }
    }

    public setCurrentUser(currentUser: CurrentUser | null): boolean {

        try {
            if (currentUser === null) {
                localStorage.setItem(this._id + LocalStorageKey.EMAIL, '');
                localStorage.setItem(this._id + LocalStorageKey.ACCESS, '');
            } else {
                localStorage.setItem(this._id + LocalStorageKey.EMAIL, currentUser.email);
                localStorage.setItem(this._id + LocalStorageKey.ACCESS, currentUser.access.join(',')); 
            }

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearCurrentUser(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.EMAIL);
            localStorage.removeItem(this._id + LocalStorageKey.ACCESS);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getLastActivity(): Date | null {

        try {
            const lastActivityString: string | null = localStorage.getItem(this._id + LocalStorageKey.LAST_ACTIVITY);
            
            return datetimeStandardParser(lastActivityString, 0);
        } catch (e: unknown) {
            return null;
        }
    }

    public setLastActivity(lastActivity: Date | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.LAST_ACTIVITY, lastActivity === null ? '' : datetimeToApiString(lastActivity, 0));

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearLastActivity(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.LAST_ACTIVITY);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getTheme(): AppTheme | null {

        try {
            const themeString: string | null = localStorage.getItem(this._id + LocalStorageKey.THEME);

            if (themeString === null) {
                return null;
            }

            return JSON.parse(themeString);
        } catch (e: unknown) {
            return null;
        }
    }

    public setTheme(theme: AppTheme): boolean {

        try { 
            localStorage.setItem(this._id + LocalStorageKey.THEME, JSON.stringify(theme));

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearTheme(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.THEME);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getToken(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.TOKEN);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setToken(token: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.TOKEN, token ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearToken(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.TOKEN);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getExpiration(): Date | null {

        try {
            const stringExpiration: string | null = localStorage.getItem(this._id + LocalStorageKey.EXPIRATION);

            return datetimeStandardParser(stringExpiration, 0);
        } catch (e: unknown) {
            return null;
        }
    }

    public setExpiration(date: Date | null): boolean {

        try {
            if (date === null) {
                localStorage.setItem(this._id + LocalStorageKey.EXPIRATION, '');
            } else {
                localStorage.setItem(this._id + LocalStorageKey.EXPIRATION, datetimeToApiString(date, 0));
            }

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearExpiration(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.EXPIRATION);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }
}

export default LocalStorageService;